import { useState, useEffect } from 'react';
import apiClient from '../../utils/api';
import { COOWN_PROJECT_START_YEAR } from '../../config/constants';
import PaginationBar from '../shared/PaginationBar';
import PlatformActivityChart from './PlatformActivityChart';
import BrowseListedDateFilter from '../listings/BrowseListedDateFilter';
import { getUserCountryLabel } from '../../data/countries';
import { getUserDisplayName, getUserInitials, getUserAvatarUrl } from '../../utils/properties';
import { downloadRegisteredUserProfileCsv } from '../../utils/adminRegisteredUsers';

const PAGE_SIZE = 10;

const METRICS = [
  { key: 'registeredUsers', label: 'Registered Users' },
  { key: 'kycApproved', label: 'KYC Approved' },
  { key: 'pendingKyc', label: 'Pending KYC' },
  { key: 'flaggedForReview', label: 'Flagged for Review', danger: true },
  { key: 'totalTransactions', label: 'Total Transactions' },
  { key: 'reconciliationIssues', label: 'Reconciliation Issues', successZero: true },
];

const KYC_FILTERS = [
  { key: 'all', label: 'All users', icon: 'fa-users' },
  { key: 'verified', label: 'Verified', icon: 'fa-check-circle' },
  { key: 'unverified', label: 'Unverified', icon: 'fa-hourglass-half' },
];

const kycStatusLabel = (status) => {
  if (status === 'approved') return 'Verified';
  if (status === 'rejected') return 'Rejected';
  return 'Unverified';
};

const kycStatusClass = (status) => {
  if (status === 'approved') return 'status-badge status-approved';
  if (status === 'rejected') return 'status-badge status-rejected';
  return 'status-badge status-pending';
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

const formatTimestamp = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const formatWalletTxType = (type) => {
  const t = String(type || '').toLowerCase();
  if (t === 'management_fee') return 'Management fee';
  if (t === 'deposit') return 'Deposit';
  if (t === 'investment') return 'Investment';
  if (t === 'sell_primary_return') return 'Sell (return to offering)';
  if (t === 'rental_payout' || t === 'dividend' || t === 'distribution') return 'Distribution';
  if (t === 'marketplace_buy') return 'Marketplace buy';
  if (t === 'marketplace_sell') return 'Marketplace sell';
  return type || 'Transaction';
};

const RegisteredUserDetailModal = ({ detail, loading, onClose, onEmailUpdated, addToast }) => {
  const [emailDraft, setEmailDraft] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => {
    setEmailDraft(detail?.user?.email || '');
  }, [detail?.user?.uid, detail?.user?.email]);

  if (!detail && !loading) return null;
  const user = detail?.user;
  const summary = detail?.portfolioSummary;
  const investments = detail?.investments || [];
  const transactions = detail?.walletTransactions || [];
  const profilePhotoUrl = detail?.profilePhotoUrl || getUserAvatarUrl(user);
  const kycVerifiedAt = detail?.kycVerifiedAt;
  const kycReviewerLabel = detail?.kycReviewerLabel;
  const emailChanged = user && emailDraft.trim().toLowerCase() !== String(user.email || '').trim().toLowerCase();

  const handleSaveEmail = async () => {
    if (!user?.uid || !emailDraft.trim()) {
      addToast?.('Enter a valid email address.', 'error');
      return;
    }
    setSavingEmail(true);
    try {
      const res = await apiClient.updateUserEmailForAdmin(user.uid, emailDraft.trim());
      addToast?.('Account email updated.', 'success');
      onEmailUpdated?.(res.data);
    } catch (err) {
      addToast?.(err.message || 'Could not update email', 'error');
    } finally {
      setSavingEmail(false);
    }
  };

  return (
    <div className="modal-overlay active" onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className="modal admin-registered-user-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="registered-user-detail-title">
        <div className="admin-registered-user-modal__header">
          <div className="admin-registered-user-modal__identity">
            {user ? (
              <>
                {profilePhotoUrl ? (
                  <img src={profilePhotoUrl} alt="" className="admin-registered-user-modal__avatar-img" />
                ) : (
                  <div className="admin-registered-user-modal__avatar" aria-hidden="true">{getUserInitials(user)}</div>
                )}
                <div>
                  <span className={kycStatusClass(user.kycStatus)}>{kycStatusLabel(user.kycStatus)}</span>
                  <h2 id="registered-user-detail-title">{getUserDisplayName(user)}</h2>
                  <p className="admin-registered-user-modal__subtitle">{user.email || '—'}</p>
                  <p className="admin-registered-user-modal__subtitle">
                    {user.countryLabel || getUserCountryLabel(user.country) || '—'}
                    {' · '}
                    {user.role || 'investor'}
                  </p>
                  <p className="admin-registered-user-modal__subtitle">
                    Registered {formatTimestamp(user.createdAt)}
                    {kycVerifiedAt ? (
                      <>
                        {' · '}
                        Verified {formatTimestamp(kycVerifiedAt)}
                      </>
                    ) : null}
                  </p>
                </div>
              </>
            ) : (
              <h2 id="registered-user-detail-title">User profile</h2>
            )}
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="modal-body admin-registered-user-modal__body">
          {loading && !detail ? (
            <p className="admin-registered-user-modal__loading"><i className="fas fa-spinner fa-spin"></i> Loading profile…</p>
          ) : null}
          {detail ? (
            <>
              <div className="admin-registered-user-modal__metrics">
                <div className="admin-registered-user-modal__metric">
                  <label>Wallet balance</label>
                  <value>${Number(summary?.walletBalanceUsd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</value>
                </div>
                <div className="admin-registered-user-modal__metric">
                  <label>Portfolio invested</label>
                  <value>${Number(summary?.totalInvestedUsd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</value>
                </div>
                <div className="admin-registered-user-modal__metric">
                  <label>Holdings</label>
                  <value>{Number(summary?.holdingsCount || 0).toLocaleString()}</value>
                </div>
                <div className="admin-registered-user-modal__metric">
                  <label>Account</label>
                  <value>{user.isActive === false ? 'Blocked' : 'Active'}</value>
                </div>
              </div>

              <div className="admin-registered-user-modal__section">
                <div className="admin-registered-user-modal__section-head">
                  <h3><i className="fas fa-id-card"></i> Profile</h3>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => downloadRegisteredUserProfileCsv(detail)}
                  >
                    <i className="fas fa-download" aria-hidden="true"></i> Download profile CSV
                  </button>
                </div>
                <div className="admin-registered-user-modal__details">
                  <div><strong>User ID:</strong> {user.uid}</div>
                  <div className="admin-registered-user-modal__email-edit">
                    <label htmlFor="admin-user-email"><strong>Account email</strong></label>
                    <div className="admin-registered-user-modal__email-row">
                      <input
                        id="admin-user-email"
                        type="email"
                        className="browse-field-input"
                        value={emailDraft}
                        onChange={(e) => setEmailDraft(e.target.value)}
                        disabled={savingEmail || user.role === 'admin'}
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={handleSaveEmail}
                        disabled={savingEmail || !emailChanged || user.role === 'admin'}
                      >
                        {savingEmail ? 'Saving…' : 'Save email'}
                      </button>
                    </div>
                    {user.role === 'admin' ? (
                      <p className="admin-registered-user-modal__photo-note">Administrator emails cannot be changed here.</p>
                    ) : (
                      <p className="admin-registered-user-modal__photo-note">
                        Updates Firebase sign-in and the registered users directory. Listing agent emails are edited separately on each property.
                      </p>
                    )}
                  </div>
                  <div><strong>Currency:</strong> {user.currency || '—'}</div>
                  <div><strong>Phone:</strong> {user.phone || '—'}</div>
                  <div><strong>Registered:</strong> {formatTimestamp(user.createdAt)}</div>
                  <div>
                    <strong>Verified:</strong>{' '}
                    {kycVerifiedAt
                      ? `${formatTimestamp(kycVerifiedAt)}${kycReviewerLabel ? ` · ${kycReviewerLabel}` : ''}`
                      : '—'}
                  </div>
                </div>
              </div>

              {profilePhotoUrl ? (
                <div className="admin-registered-user-modal__section">
                  <h3><i className="fas fa-camera"></i> Profile photo</h3>
                  <p className="admin-registered-user-modal__photo-note">
                    Profile image (admin only). Google sign-in photos are lower resolution until the user uploads their own.
                  </p>
                  <a
                    href={profilePhotoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="admin-registered-user-modal__photo-link"
                  >
                    <img
                      src={profilePhotoUrl}
                      alt={`${getUserDisplayName(user)} profile`}
                      className="admin-registered-user-modal__photo-large"
                    />
                  </a>
                  <div className="admin-registered-user-modal__photo-actions">
                    <a
                      href={profilePhotoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline btn-sm"
                    >
                      <i className="fas fa-external-link-alt" aria-hidden="true"></i> Open full size
                    </a>
                    <a
                      href={profilePhotoUrl}
                      download={`${user.uid}-profile`}
                      className="btn btn-outline btn-sm"
                    >
                      <i className="fas fa-download" aria-hidden="true"></i> Download
                    </a>
                  </div>
                </div>
              ) : null}

              <div className="admin-registered-user-modal__section">
                <h3><i className="fas fa-chart-pie"></i> Portfolio</h3>
                {investments.length === 0 ? (
                  <p className="admin-registered-user-modal__empty">No active holdings.</p>
                ) : (
                  <div className="admin-table-wrap">
                    <table className="investment-table investment-table--registered-user-holdings">
                      <thead>
                        <tr>
                          <th>Property</th>
                          <th>Shares</th>
                          <th>Invested</th>
                          <th>Purchased</th>
                        </tr>
                      </thead>
                      <tbody>
                        {investments.map((inv) => (
                          <tr key={inv.id}>
                            <td>
                              <strong>{inv.propertyTitle}</strong>
                              {inv.propertyLocation ? (
                                <div className="admin-registered-user-modal__subline">{inv.propertyLocation}</div>
                              ) : null}
                            </td>
                            <td>{inv.shares.toLocaleString()}</td>
                            <td>${inv.investedUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td>{formatDate(inv.purchaseDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="admin-registered-user-modal__section">
                <h3><i className="fas fa-wallet"></i> Recent wallet activity</h3>
                {transactions.length === 0 ? (
                  <p className="admin-registered-user-modal__empty">No wallet transactions yet.</p>
                ) : (
                  <div className="admin-table-wrap">
                    <table className="investment-table investment-table--registered-user-holdings">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((tx) => (
                          <tr key={tx.id}>
                            <td>{formatDate(tx.createdAt)}</td>
                            <td>{formatWalletTxType(tx.type)}</td>
                            <td>${Number(tx.amountUsd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const AdminPlatformOverview = ({ addToast }) => {
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [userStats, setUserStats] = useState({ total: 0, verified: 0, unverified: 0 });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
    hasPrev: false,
    hasNext: false,
    startIndex: 0,
    endIndex: 0,
  });
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [kycFilter, setKycFilter] = useState('all');
  const [registeredDateMode, setRegisteredDateMode] = useState('all');
  const [registeredDateYear, setRegisteredDateYear] = useState('');
  const [registeredDateMonth, setRegisteredDateMonth] = useState('');
  const [registeredDateDay, setRegisteredDateDay] = useState('');
  const [page, setPage] = useState(1);
  const [chartType, setChartType] = useState('pie');
  const [selectedUid, setSelectedUid] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadMetrics = async () => {
    setLoading(true);
    try {
      const res = await apiClient.getPlatformActivity();
      setMetrics(res.data || null);
    } catch (err) {
      addToast(err.message || 'Could not load platform metrics', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const res = await apiClient.listUsersForAdmin({
        search: searchQuery,
        page,
        limit: PAGE_SIZE,
        kycFilter,
        dateMode: registeredDateMode,
        dateYear: registeredDateYear,
        dateMonth: registeredDateMonth,
        dateDay: registeredDateDay,
      });
      const payload = res.data || {};
      setUsers(Array.isArray(payload.items) ? payload.items : []);
      setUserStats(payload.stats || { total: 0, verified: 0, unverified: 0 });
      setPagination(payload.pagination || {
        page: 1,
        limit: PAGE_SIZE,
        total: 0,
        totalPages: 1,
        hasPrev: false,
        hasNext: false,
        startIndex: 0,
        endIndex: 0,
      });
    } catch (err) {
      const message = err.message || 'Could not load registered users';
      setUsers([]);
      setUsersError(message);
      addToast(message, 'error');
    } finally {
      setUsersLoading(false);
    }
  };

  const loadUserDetail = async (uid) => {
    setDetailLoading(true);
    try {
      const res = await apiClient.getUserForAdmin(uid);
      setUserDetail(res.data || null);
    } catch (err) {
      addToast(err.message || 'Could not load user profile', 'error');
      setUserDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshAll = async () => {
    await Promise.all([loadMetrics(), loadUsers()]);
  };

  useEffect(() => { loadMetrics(); }, []);
  useEffect(() => {
    setSelectedUid(null);
    setUserDetail(null);
    loadUsers();
  }, [searchQuery, page, kycFilter, registeredDateMode, registeredDateYear, registeredDateMonth, registeredDateDay]);

  useEffect(() => {
    if (selectedUid) loadUserDetail(selectedUid);
    else setUserDetail(null);
  }, [selectedUid]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await apiClient.downloadUsersExport({ search: searchQuery });
      addToast('Registered users CSV downloaded.', 'success');
    } catch (err) {
      addToast(err.message || 'CSV export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  const kycFilterCount = (key) => {
    if (key === 'verified') return userStats.verified || 0;
    if (key === 'unverified') return userStats.unverified || 0;
    return userStats.total || 0;
  };

  const browseFiltersActive = kycFilter !== 'all'
    || searchQuery.trim() !== ''
    || registeredDateMode !== 'all';

  return (
    <div>
      <div className="admin-panel-head">
        <div>
          <h3><i className="fas fa-chart-pie"></i> Platform activity</h3>
          <p className="admin-panel-sub">Live counts from users, KYC, wallet transactions, and listing reconciliation.</p>
        </div>
        <button type="button" className="btn btn-outline btn-sm" onClick={refreshAll} disabled={loading || usersLoading}>
          <i className={`fas fa-sync${loading || usersLoading ? ' fa-spin' : ''}`}></i> Refresh
        </button>
      </div>

      {loading && !metrics ? (
        <p style={{ color: 'var(--text-light)', marginTop: '1rem' }}>Loading platform metrics…</p>
      ) : (
        <div className="platform-activity-layout">
          <div className="detail-metrics platform-activity-metrics">
            {METRICS.map((metric) => {
              const value = metrics?.[metric.key] ?? 0;
              let valueStyle;
              if (metric.danger && value > 0) valueStyle = { color: 'var(--danger)' };
              if (metric.successZero) valueStyle = { color: value === 0 ? 'var(--success)' : 'var(--danger)' };

              return (
                <div key={metric.key} className="detail-metric">
                  <label>{metric.label}</label>
                  <value style={valueStyle}>{Number(value).toLocaleString()}</value>
                </div>
              );
            })}
          </div>
          <PlatformActivityChart
            metrics={metrics}
            metricDefs={METRICS}
            chartType={chartType}
            onChartTypeChange={setChartType}
          />
        </div>
      )}

      <div className="admin-panel-head" style={{ marginTop: '2rem' }}>
        <div>
          <h3><i className="fas fa-users"></i> Registered users</h3>
          <p className="admin-panel-sub">
            Compact queue — click a row to open profile, portfolio, and wallet activity. CSV export includes the full catalog.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={handleExport}
          disabled={exporting || usersLoading}
          title="Download every registered user as a catalog CSV for Excel"
        >
          <i className={`fas fa-download${exporting ? ' fa-spin' : ''}`}></i> Download CSV (all users)
        </button>
      </div>

      <div className="registered-users-toolbar" aria-label="Filter registered users">
        <span className="live-listings-toolbar__label" id="registered-users-kyc-label">KYC status</span>
        <div
          className="listings-view-toggle registered-users-kyc-toggle"
          role="tablist"
          aria-labelledby="registered-users-kyc-label"
        >
          {KYC_FILTERS.map((box) => {
            const active = kycFilter === box.key;
            return (
              <button
                key={box.key}
                type="button"
                role="tab"
                aria-selected={active}
                className={active ? 'active' : ''}
                onClick={() => { setKycFilter(box.key); setPage(1); }}
              >
                <i className={`fas ${box.icon}`} aria-hidden="true"></i>
                <span>{box.label}</span>
                <span className="admin-chat-reports-period-count">{kycFilterCount(box.key).toLocaleString()}</span>
              </button>
            );
          })}
        </div>
        <p className="registered-users-toolbar__hint">
          {kycFilter === 'all'
            ? `Browsing all ${(userStats.total || 0).toLocaleString()} registered users.`
            : kycFilter === 'verified'
              ? `Showing ${(userStats.verified || 0).toLocaleString()} verified users.`
              : `Showing ${(userStats.unverified || 0).toLocaleString()} users who still need verification.`}
        </p>
        <BrowseListedDateFilter
          variant="admin"
          useCalendarInputs
          fieldLabel="Registered"
          yearMin={COOWN_PROJECT_START_YEAR}
          dateMode={registeredDateMode}
          onDateModeChange={(mode) => { setRegisteredDateMode(mode); setPage(1); }}
          listedYear={registeredDateYear}
          onListedYearChange={(v) => { setRegisteredDateYear(v); setPage(1); }}
          listedMonth={registeredDateMonth}
          onListedMonthChange={(v) => { setRegisteredDateMonth(v); setPage(1); }}
          listedDate={registeredDateDay}
          onListedDateChange={(v) => { setRegisteredDateDay(v); setPage(1); }}
          onClear={() => {
            setRegisteredDateMode('all');
            setRegisteredDateYear('');
            setRegisteredDateMonth('');
            setRegisteredDateDay('');
            setPage(1);
          }}
        />
      </div>

      <div className="submission-queue-toolbar" style={{ marginBottom: '1rem' }}>
        <div className="submission-queue-search">
          <div className="submission-queue-search-wrap">
            <i className="fas fa-search" aria-hidden="true"></i>
            <input
              type="text"
              inputMode="search"
              autoComplete="off"
              spellCheck={false}
              placeholder="Search name, email, country, role…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search registered users"
            />
            {searchInput.trim() ? (
              <button
                type="button"
                className="submission-queue-search-clear"
                aria-label="Clear search"
                onClick={() => setSearchInput('')}
              >
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {browseFiltersActive ? (
        <p className="live-listings-analytics-note">
          Table shows <strong>{pagination.total.toLocaleString()}</strong> users matching your filters.
          CSV export still includes the full catalog ({(userStats.total || 0).toLocaleString()}).
        </p>
      ) : null}

      <div className="admin-table-wrap">
        <table className="investment-table investment-table--registered-users">
          <thead>
            <tr>
              <th>User</th>
              <th>Country</th>
              <th>Role</th>
              <th>KYC</th>
              <th>Account</th>
              <th>Balance</th>
              <th>Registered</th>
              <th>Verified</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {usersLoading && users.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
                  Loading registered users…
                </td>
              </tr>
            ) : null}
            {!usersLoading && users.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
                  {usersError ? (
                    <>
                      <strong style={{ color: 'var(--danger)', display: 'block', marginBottom: '0.5rem' }}>
                        Could not load registered users
                      </strong>
                      <span>{usersError}</span>
                    </>
                  ) : searchQuery.trim() || kycFilter !== 'all' ? (
                    'No users match your filters.'
                  ) : (
                    'No registered users yet.'
                  )}
                </td>
              </tr>
            ) : null}
            {users.map((user) => {
              const name = getUserDisplayName(user);
              const avatarUrl = user.profilePhotoUrl || getUserAvatarUrl(user);
              return (
                <tr
                  key={user.uid}
                  className={`admin-registered-user-row${selectedUid === user.uid ? ' is-selected' : ''}`}
                  onClick={() => setSelectedUid(user.uid)}
                >
                  <td className="live-listing-deal-cell">
                    <div className="admin-registered-user-row__identity">
                      {avatarUrl ? (
                        <img src={avatarUrl} alt="" className="admin-registered-user-row__avatar-img" />
                      ) : (
                        <div className="admin-registered-user-row__avatar" aria-hidden="true">{getUserInitials(user)}</div>
                      )}
                      <div className="admin-registered-user-row__text">
                        <div className="live-listing-deal-line">
                          <strong className="live-listing-deal-title">{name}</strong>
                        </div>
                        <div className="live-listing-deal-sub admin-registered-user-row__email">{user.email || '—'}</div>
                      </div>
                    </div>
                  </td>
                  <td>{user.countryLabel || getUserCountryLabel(user.country) || '—'}</td>
                  <td>{user.role || 'investor'}</td>
                  <td>
                    <span className={kycStatusClass(user.kycStatus)}>
                      {kycStatusLabel(user.kycStatus)}
                    </span>
                  </td>
                  <td>
                    <span className={user.isActive === false ? 'status-badge status-rejected' : 'status-badge status-approved'}>
                      {user.isActive === false ? 'Blocked' : 'Active'}
                    </span>
                  </td>
                  <td>${Number(user.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>{formatDate(user.kycVerifiedAt)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm live-listing-manage-btn"
                      onClick={(e) => { e.stopPropagation(); setSelectedUid(user.uid); }}
                    >
                      Open
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <PaginationBar
        page={pagination.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        startIndex={pagination.startIndex}
        endIndex={pagination.endIndex}
        hasPrev={pagination.hasPrev}
        hasNext={pagination.hasNext}
        onPageChange={setPage}
        itemLabel="users"
        compact
      />

      <RegisteredUserDetailModal
        detail={userDetail}
        loading={detailLoading}
        addToast={addToast}
        onClose={() => setSelectedUid(null)}
        onEmailUpdated={(updatedUser) => {
          if (!updatedUser) return;
          setUserDetail((prev) => (prev ? { ...prev, user: { ...prev.user, ...updatedUser } } : prev));
          setUsers((prev) => prev.map((row) => (row.uid === updatedUser.uid ? { ...row, ...updatedUser } : row)));
        }}
      />
    </div>
  );
};

export default AdminPlatformOverview;
